import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useProgress } from '../../contexts/ProgressContext';
import { StoryService } from '../../services/storyService';
import { updateProfile } from 'firebase/auth';
import { doc, updateDoc, getDoc, setDoc, collection, getDocs, query, where, limit, getCountFromServer } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase/firebase'; 
import { syncPublicProfile } from '../../firebase/publicProfile';
import Notification from './Notification';
import FollowButton from './FollowButton';
import { useSafetyCheck } from '../../hooks/useSafetyCheck';
import { containsHiddenProfanity } from '../../utils/userNameCheck';

const C = {
    primary: '#6a37d4', primaryLight: '#ae8dff', primaryContainer: '#ede8ff',
    secondary: '#006947', secondaryContainer: '#d1fae5',
    tertiary: '#6f5900', tertiaryContainer: '#fed01b',
    surface: '#f3f6ff', surfaceLowest: '#ffffff', surfaceContainer: '#dce9fd', surfaceHigh: '#d6e3f9',
    onBackground: '#252f3c', onSurfaceVariant: '#525c6a', outlineVariant: '#a4aebe',
};

const HEADING = "'Epilogue', sans-serif";

const SectionTitle: React.FC<{ icon: string; title: string; extra?: React.ReactNode }> = ({ icon, title, extra }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', padding: '0 4px' }}>
        <span style={{ fontSize: '1.2rem' }}>{icon}</span>
        <h2 style={{ fontFamily: HEADING, fontWeight: 900, fontSize: '1.3rem', color: C.onBackground, margin: 0 }}>{title}</h2>
        {extra}
    </div>
);

const StatCard: React.FC<{ label: string; value: string | number; icon: string; tint: string }> = ({ label, value, icon, tint }) => (
    <div style={{ background: C.surfaceLowest, borderRadius: '20px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: C.onSurfaceVariant }}>{label}</span>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: tint, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem' }}>{icon}</div>
        </div>
        <div style={{ fontFamily: HEADING, fontWeight: 900, fontSize: '1.8rem', color: C.onBackground, marginTop: '12px' }}>{value}</div>
    </div>
);

const SettingRow: React.FC<{ icon: string; tint: string; title: string; subtitle: string; onClick: () => void }> = ({ icon, tint, title, subtitle, onClick }) => (
    <button onClick={onClick} style={{ width: '100%', background: C.surfaceLowest, border: 'none', borderRadius: '20px', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', textAlign: 'left', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', fontFamily: 'inherit' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: tint, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>{icon}</div>
            <div>
                <div style={{ fontFamily: HEADING, fontWeight: 800, fontSize: '0.95rem', color: C.onBackground }}>{title}</div>
                <div style={{ fontSize: '0.78rem', color: C.onSurfaceVariant }}>{subtitle}</div>
            </div>
        </div>
        <span style={{ color: C.outlineVariant, fontSize: '1.4rem' }}>›</span>
    </button>
);

interface FollowedWriter {
    id: string;
    penName: string;
    avatar: string;
    storyCount: number;
}

const Profile: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { totalWordCount, xp, achievements, level, streak } = useProgress();
    const [storyCount, setStoryCount] = useState(0);
    const [followedWriters, setFollowedWriters] = useState<FollowedWriter[]>([]);
    const [followingLoaded, setFollowingLoaded] = useState(false);
    const [followerCount, setFollowerCount] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768); 

    const { runPublishCheck } = useSafetyCheck();
    const [saving, setSaving] = useState(false);
    const [safetyMessage, setSafetyMessage] = useState('');

    const [notification, setNotification] = useState<{
        show: boolean;
        type: 'save' | 'publish' | 'unpublish' | 'republish' | 'tags' | 'error' | 'password-reset' | 'profile-updated' | 'privacy-updated';
        storyTitle?: string;
        errorMessage?: string;
    }>({ show: false, type: 'save' });
    
    
    // Modal states
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);

    
    // Edit profile fields
    const [displayName, setDisplayName] = useState(currentUser?.displayName || '');
    const [selectedAvatar, setSelectedAvatar] = useState('👤');
    const [bio, setBio] = useState('');
    
    // Privacy settings
    const [storyVisibility, setStoryVisibility] = useState<'public' | 'friends' | 'private'>('public');
    // const [commentsEnabled, setCommentsEnabled] = useState(true);
    const [profileVisible, setProfileVisible] = useState(true);

   

    const username = currentUser?.displayName || 'username';

    const avatarOptions = ['👤', '🐱', '🐶', '🦊', '🐻', '🐼', '🦁', '🐯', '🐸', '🐨', '🦄', '🦋', '🌟', '⚡', '🎨', '📚'];

 // Mobile
 useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
}, []);


// Load story count and user settings
useEffect(() => {
    const loadData = async () => {
        if (currentUser) {
            try {
                const stories = await StoryService.getUserStories(currentUser.uid);
                setStoryCount(stories.length);
                
                // Load user settings from Firestore
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    setSelectedAvatar(data.avatar || '👤');
                    setBio(data.bio || '');
                    setStoryVisibility(data.storyVisibility || 'public');
                   // setCommentsEnabled(data.commentsEnabled ?? true);
                    setProfileVisible(data.profileVisible ?? true);
                    setDisplayName(data.displayName || currentUser.displayName || '');
                } else {
                    // Create initial user document if it doesn't exist
                    await setDoc(doc(db, 'users', currentUser.uid), {
                        displayName: currentUser.displayName || 'User',
                        email: currentUser.email,
                        avatar: '👤',
                        bio: '',
                        storyVisibility: 'public',
                        commentsEnabled: true,
                        profileVisible: true,
                        createdAt: new Date(),
                        updatedAt: new Date()
                    });
                    setDisplayName(currentUser.displayName || '');
                }
            } catch (error) {
                console.error('Error loading data:', error);
            }
        }
    };
    loadData();
}, [currentUser]);

// Load writers this user follows
useEffect(() => {
    if (!currentUser) return;
    const loadFollowing = async () => {
        try {
            const snap = await getDocs(
                query(collection(db, 'follows'), where('followerId', '==', currentUser.uid), limit(30))
            );
            const writers = await Promise.all(snap.docs.map(async (d) => {
                const id = d.data().followedId as string;
                const storiesQ = query(collection(db, 'publishedStories'), where('userId', '==', id));
                const [profileSnap, countSnap, firstStory] = await Promise.all([
                    getDoc(doc(db, 'publicProfiles', id)),
                    getCountFromServer(storiesQ),
                    getDocs(query(storiesQ, limit(1)))
                ]);
                const pub: any = profileSnap.exists() ? profileSnap.data() : {};
                const story: any = firstStory.docs[0]?.data() || {};
                return {
                    id,
                    penName: pub.penName || story.authorPseudonym || 'Writer',
                    avatar: pub.avatar || story.authorAvatar || '👤',
                    storyCount: countSnap.data().count
                } as FollowedWriter;
            }));
            setFollowedWriters(writers);
        } catch (error) {
            console.error('Error loading following:', error);
        } finally {
            setFollowingLoaded(true);
        }
    };
    loadFollowing();
}, [currentUser]);

// Load how many followers this user has
useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, 'publicProfiles', currentUser.uid))
        .then(snap => setFollowerCount(snap.exists() ? (snap.data().followerCount ?? 0) : 0))
        .catch(console.error);
}, [currentUser]);

const handleSaveClick = async () => {
    if (saving) return;
    setSaving(true);
    setSafetyMessage('');
    try {
        if (containsHiddenProfanity(displayName)) {
            setSafetyMessage("Please choose a different name. That one has a word we don't allow.");
            return;
        }
        const safety = await runPublishCheck({ title: displayName, description: bio }, false);

        if (!safety.passed) {
            setSafetyMessage(safety.issues.map(i => i.detail).join(' '));
            return;
        }
        // Stories get queued for a teacher when the AI flags them. A bio goes live
        // immediately and has no review queue, so we block it instead.
        if (safety.needsReview) {
            setSafetyMessage("We couldn't approve that name or bio. Try changing the wording and save again!");
            return;
        }

        await handleSaveProfile();
    } finally {
        setSaving(false);
    }
};

// Save profile changes
const handleSaveProfile = async () => {
    if (!currentUser) return;
    
    console.log('🔍 Starting profile save...');
    console.log('User ID:', currentUser.uid);
    console.log('New displayName:', displayName);
    console.log('New avatar:', selectedAvatar);
    
    try {
        // Update Firebase Auth display name if changed
        if (displayName && displayName !== currentUser.displayName) {
            await updateProfile(currentUser, { displayName });
            console.log('✅ Auth display name updated');
        }
        
        // Update Firestore user document
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, {
            displayName: displayName || currentUser.displayName,
            avatar: selectedAvatar,
            bio: bio.slice(0, 50),
            updatedAt: new Date()
        });
        console.log('✅ User document updated');
        await syncPublicProfile(currentUser.uid, {
            penName: displayName || currentUser.displayName || '',
            avatar: selectedAvatar,
            bio: bio.slice(0, 50),
            profileVisible
        });
        
        // UPDATE ALL PUBLISHED STORIES
        console.log('🔍 Looking for published stories...');
        const publishedStoriesRef = collection(db, 'publishedStories');
        const userStoriesQuery = query(publishedStoriesRef, where('userId', '==', currentUser.uid));
        const userStoriesSnapshot = await getDocs(userStoriesQuery);
        
        console.log('📚 Found stories:', userStoriesSnapshot.docs.length);
        
        if (userStoriesSnapshot.docs.length > 0) {
            // Update each published story
            const updatePromises = userStoriesSnapshot.docs.map(storyDoc => {
                console.log('📝 Updating story:', storyDoc.id);
                return updateDoc(doc(db, 'publishedStories', storyDoc.id), {
                    authorPseudonym: displayName || currentUser.displayName,
                    authorAvatar: selectedAvatar
                });
            });
            
            await Promise.all(updatePromises);
            console.log('✅ All stories updated!');
                }
                
                setShowEditModal(false);
                setNotification({ 
                    show: true, 
                    type: 'profile-updated' 
                });
            } catch (error: any) {
                console.error('❌ Error saving profile:', error);
                console.error('Error code:', error.code);
                console.error('Error message:', error.message);
                
                setShowEditModal(false);
                setNotification({ 
                    show: true, 
                    type: 'error',
                    errorMessage: error.message || 'Failed to update profile. Please try again.'
                });
            }
        }; 

    // Save privacy settings
// Save privacy settings
const handleSavePrivacy = async () => {
    if (!currentUser) return;
    
    const userRef = doc(db, 'users', currentUser.uid);
    
    try {
        await updateDoc(userRef, {
            storyVisibility,
            profileVisible,
            updatedAt: new Date()
        });

        await syncPublicProfile(currentUser.uid, { profileVisible });
        
        setShowPrivacyModal(false);
        setNotification({ 
            show: true, 
            type: 'privacy-updated' 
        });
    } catch (error: any) {
        console.error('Error saving privacy:', error);
        
        if (error.code === 'not-found') {
            try {
                await setDoc(userRef, {
                    displayName: currentUser.displayName || 'User',
                    avatar: selectedAvatar,
                    storyVisibility,
                    profileVisible,
                    createdAt: new Date(),
                    updatedAt: new Date()
                });
                setShowPrivacyModal(false);
                setNotification({ 
                    show: true, 
                    type: 'privacy-updated' 
                });
            } catch (createError) {
                console.error('Error creating profile:', createError);
                setShowPrivacyModal(false);
                setNotification({ 
                    show: true, 
                    type: 'error',
                    errorMessage: 'Failed to save settings. Please try again.'
                });
            }
        } else {
            setShowPrivacyModal(false);
            setNotification({ 
                show: true, 
                type: 'error',
                errorMessage: error.message || 'Failed to save settings. Please try again.'
            });
        }
    }
};

// Add this new function
const handleRequestPasswordReset = async () => {
    if (!currentUser?.email) {
        setNotification({
            show: true,
            type: 'error',
            errorMessage: 'No email found for this account!'
        });
        return;
    }
    
    // Send reset email immediately - no confirmation needed
    try {
        await sendPasswordResetEmail(auth, currentUser.email);
        setNotification({
            show: true,
            type: 'password-reset',
            storyTitle: currentUser.email,  // Pass the email here
            errorMessage: undefined
        });
    } catch (error: any) {
        console.error('Error sending reset email:', error);
        
        if (error.code === 'auth/too-many-requests') {
            setNotification({
                show: true,
                type: 'error',
                errorMessage: 'Too many requests. Please wait a few minutes and try again.'
            });
        } else {
            setNotification({
                show: true,
                type: 'error',
                errorMessage: 'Failed to send reset email. Please try again.'
            });
        }
    }
};


        

return (
    <div style={{
        background: C.surface,
        minHeight: '100vh',
        padding: isMobile ? '20px 16px 80px' : '40px 48px 60px',
        fontFamily: "'Plus Jakarta Sans', sans-serif"
    }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: isMobile ? '28px' : '36px' }}>

            {/* Hero */}
            <div style={{
                position: 'relative', overflow: 'hidden', background: C.surfaceLowest, borderRadius: '24px',
                padding: isMobile ? '20px' : '28px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                display: 'flex', flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: '16px'
            }}>
                <div style={{ position: 'absolute', right: -32, top: -32, width: 144, height: 144, borderRadius: '50%', background: `${C.primaryLight}33`, filter: 'blur(24px)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', left: -32, bottom: -32, width: 112, height: 112, borderRadius: '50%', background: `${C.tertiaryContainer}4d`, filter: 'blur(20px)', pointerEvents: 'none' }} />

                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '18px', minWidth: 0 }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                        <div style={{
                            width: 84, height: 84, borderRadius: '50%',
                            background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '2.6rem', boxShadow: '0 4px 12px rgba(106,55,212,0.25)'
                        }}>
                            {selectedAvatar}
                        </div>
                        <div style={{
                            position: 'absolute', bottom: -4, right: -4, background: C.tertiaryContainer, color: C.tertiary,
                            padding: '2px 8px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 800, fontFamily: HEADING
                        }}>
                            ⭐ Lv {level}
                        </div>
                    </div>

                    <div style={{ minWidth: 0 }}>
                        <h1 style={{
                            fontFamily: HEADING, fontWeight: 900, fontSize: isMobile ? '1.4rem' : '1.8rem',
                            color: C.onBackground, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}>
                            @{username}
                        </h1>
                        <span style={{
                            display: 'inline-block', marginTop: 6, background: C.primaryContainer, color: C.primary,
                            padding: '3px 12px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700
                        }}>
                            Level {level} Author
                        </span>

                        <span style={{
    display: 'inline-block', marginTop: 6, marginLeft: 8, background: C.surfaceContainer, color: C.primary,
    padding: '3px 12px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 700
}}>
    👥 {followerCount} {followerCount === 1 ? 'follower' : 'followers'}
</span>

                        {bio && (
                            <p style={{ margin: '8px 0 0', fontSize: '0.9rem', color: C.onSurfaceVariant, fontWeight: 600 }}>
                                ✨ {bio}
                            </p>
                        )}
                    </div>
                </div>

                <button
                    onClick={() => setShowEditModal(true)}
                    style={{
                        position: 'relative', minHeight: 44, padding: '10px 22px', borderRadius: '9999px', border: 'none',
                        background: C.surfaceContainer, color: C.primary, fontFamily: HEADING, fontWeight: 800,
                        fontSize: '0.8rem', letterSpacing: '0.5px', cursor: 'pointer'
                    }}
                >
                    ✏️ EDIT PROFILE
                </button>
            </div>

            {/* Stats */}
            <div>
                <SectionTitle icon="📊" title="Your Stats" />
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: isMobile ? '12px' : '16px' }}>
                    <StatCard label="Total Words" value={totalWordCount.toLocaleString()} icon="✏️" tint={C.primaryContainer} />
                    <StatCard label="Stories" value={storyCount} icon="📚" tint={C.surfaceHigh} />
                    <StatCard label="XP Points" value={xp.toLocaleString()} icon="⭐" tint={`${C.tertiaryContainer}55`} />
                    <StatCard label="Writing Streak" value={`${streak} ${streak === 1 ? 'day' : 'days'}`} icon="🔥" tint="#fee2e2" />
                </div>
            </div>

            {/* Following */}
            <div>
                <SectionTitle
                    icon="👥"
                    title="Following"
                    extra={followingLoaded && followedWriters.length > 0 ? (
                        <span style={{ fontSize: '0.9rem', color: C.primary, fontWeight: 700 }}>({followedWriters.length})</span>
                    ) : undefined}
                />

                {followingLoaded && followedWriters.length === 0 ? (
                    <div style={{
                        background: C.surfaceLowest, border: `2px dashed ${C.surfaceHigh}`, borderRadius: '20px',
                        padding: isMobile ? '20px' : '28px', textAlign: 'center', color: C.onSurfaceVariant,
                        fontSize: isMobile ? '0.9rem' : '1rem', fontWeight: 500
                    }}>
                        You're not following any writers yet. Tap a writer's name on a story to visit their page and follow them!
                    </div>
                ) : (
                    <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px' }}>
                        {followedWriters.map(w => (
                            <div
                                key={w.id}
                                onClick={() => navigate(`/writer/${w.id}`)}
                                style={{
                                    minWidth: '200px', flexShrink: 0, background: C.surfaceLowest, borderRadius: '20px',
                                    padding: '18px 16px', textAlign: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                                    display: 'flex', flexDirection: 'column', alignItems: 'center'
                                }}
                            >
                                <div style={{
                                    width: 56, height: 56, borderRadius: '50%', marginBottom: 10,
                                    background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem'
                                }}>
                                    {w.avatar}
                                </div>
                                <div style={{ fontFamily: HEADING, fontWeight: 900, fontSize: '0.92rem', color: C.onBackground, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    @{w.penName}
                                </div>
                                <div style={{ fontSize: '0.72rem', color: C.onSurfaceVariant, margin: '4px 0 12px' }}>
                                    {w.storyCount} {w.storyCount === 1 ? 'story' : 'stories'} published
                                </div>
                                <div onClick={e => e.stopPropagation()}>
                                    <FollowButton targetUserId={w.id} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Achievements - keep existing */}
            {/* ... your existing achievements code ... */}

            {/* Account Settings */}
            <div>
                <SectionTitle icon="⚙️" title="Account Settings" />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <SettingRow icon="🔑" tint={`${C.tertiaryContainer}55`} title="Reset Password" subtitle="Email reset link" onClick={handleRequestPasswordReset} />
                    <SettingRow icon="🔒" tint={C.secondaryContainer} title="Privacy Settings" subtitle="Who can see your stories" onClick={() => setShowPrivacyModal(true)} />
                </div>
            </div>
        </div>

             {/* Edit Profile Modal */}
        {showEditModal && (
            <Modal onClose={() => { setSafetyMessage(''); setShowEditModal(false); }} title="✏️ Edit Your Profile" isMobile={isMobile}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div>
                            <label style={{ fontSize: '1rem', fontWeight: '600', color: '#333', display: 'block', marginBottom: '8px' }}>
                                Display Name
                            </label>
                            <input
                                type="text"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                maxLength={20}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    fontSize: '1rem',
                                    border: '2px solid #e0e0e0',
                                    borderRadius: '8px'
                                }}
                                placeholder="Enter your name"
                            />
                        </div>

                        <div>
                            <label style={{ fontSize: '1rem', fontWeight: '600', color: '#333', display: 'block', marginBottom: '12px' }}>
                                Choose Your Avatar
                            </label>
                            <div style={{
    display: 'grid',
    gridTemplateColumns: isMobile ? 'repeat(4, 1fr)' : 'repeat(8, 1fr)',
    gap: isMobile ? '6px' : '8px'
}}>
    {avatarOptions.map(avatar => (
        <button
            key={avatar}
            onClick={() => setSelectedAvatar(avatar)}
            style={{
                width: isMobile ? '100%' : '60px',
                height: isMobile ? '50px' : '60px',
                fontSize: isMobile ? '1.5rem' : '2rem',
                border: selectedAvatar === avatar ? `3px solid ${C.primary}` : '2px solid #e0e0e0',
                borderRadius: isMobile ? '8px' : '12px',
                background: selectedAvatar === avatar ? C.primaryContainer : 'white',
                cursor: 'pointer'
            }}
        >
            {avatar}
        </button>
    ))}
</div>
                        </div>

                        <div>
                            <label style={{ fontSize: '1rem', fontWeight: '600', color: '#333', display: 'block', marginBottom: '8px' }}>
                                Bio (50 characters max)
                            </label>
                            <textarea
                                value={bio}
                                onChange={(e) => setBio(e.target.value.slice(0, 50))}
                                maxLength={50}
                                rows={3}
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    fontSize: '1rem',
                                    border: '2px solid #e0e0e0',
                                    borderRadius: '8px',
                                    resize: 'none'
                                }}
                                placeholder="Tell us about yourself!"
                            />
                            <div style={{ fontSize: '0.85rem', color: '#999', marginTop: '4px', textAlign: 'right' }}>
                                {bio.length}/50
                            </div>
                        </div>

                        {safetyMessage && (
    <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: '12px', padding: '12px 16px', fontSize: '0.9rem', fontWeight: 600 }}>
        {safetyMessage}
    </div>
)}

<div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
    <button
        onClick={() => { setSafetyMessage(''); setShowEditModal(false); }}
        style={{
            flex: 1,
            padding: '14px',
            border: '2px solid #e0e0e0',
            borderRadius: '10px',
            background: 'white',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            color: '#666'
        }}
    >
        Cancel
    </button>
    <button
        onClick={handleSaveClick}
        disabled={saving}
        style={{
            flex: 1,
            padding: '14px',
            border: 'none',
            borderRadius: '10px',
            background: C.primary,
            color: 'white',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1
        }}
    >
        {saving ? 'Checking...' : 'Save Changes'}
    </button>
</div>
                    </div>
                </Modal>
            )}

            {/* Privacy Settings Modal */}
            
            {showPrivacyModal && (
                <Modal onClose={() => setShowPrivacyModal(false)} title="🔒 Privacy Settings" isMobile={isMobile}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                        <div>
                            <label style={{ fontSize: '1.1rem', fontWeight: '700', color: '#333', display: 'block', marginBottom: '12px' }}>
                                Who can see your stories?
                            </label>
                            {[
                                { value: 'public', label: '🌍 Everyone', desc: 'Anyone can read your stories' },
                                // { value: 'friends', label: '👥 Friends Only', desc: 'Only people you follow' },
                                { value: 'private', label: '🔐 Just Me', desc: 'Only you can see them' }
                            ].map(option => (
                                <button
                                    key={option.value}
                                    onClick={() => setStoryVisibility(option.value as any)}
                                    style={{
                                        width: '100%',
                                        padding: '16px',
                                        marginBottom: '10px',
                                        border: storyVisibility === option.value ? `3px solid ${C.primary}` : '2px solid #e0e0e0',
                                        borderRadius: '12px',
                                        background: storyVisibility === option.value ? C.primaryContainer : 'white',
                                        cursor: 'pointer',
                                        textAlign: 'left'
                                    }}
                                >
                                    <div style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '4px' }}>
                                        {option.label}
                                    </div>
                                    <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                        {option.desc}
                                    </div>
                                </button>
                            ))}
                        </div>
 {/* 
                        <div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '16px', border: '2px solid #e0e0e0', borderRadius: '12px' }}>
                                <input
                                    type="checkbox"
                                    checked={commentsEnabled}
                                    onChange={(e) => setCommentsEnabled(e.target.checked)}
                                    style={{ width: '24px', height: '24px' }}
                                />
                                <div>
                                    <div style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '4px' }}>
                                        💬 Allow Comments
                                    </div>
                                    <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                        Let others comment on your stories
                                    </div>
                                </div>
                            </label>
                        </div>
                        */}

                        <div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '16px', border: '2px solid #e0e0e0', borderRadius: '12px' }}>
                                <input
                                    type="checkbox"
                                    checked={profileVisible}
                                    onChange={(e) => setProfileVisible(e.target.checked)}
                                    style={{ width: '24px', height: '24px' }}
                                />
                                <div>
                                    <div style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '4px' }}>
                                        👁️ Show My Bio
                                    </div>
                                    <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                        Let others see your profile page
                                    </div>
                                </div>
                            </label>

                            
                        </div>

                        <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
                            <button
                                onClick={() => setShowPrivacyModal(false)}
                                style={{
                                    flex: 1,
                                    padding: '14px',
                                    border: '2px solid #e0e0e0',
                                    borderRadius: '10px',
                                    background: 'white',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    color: '#666'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSavePrivacy}
                                style={{
                                    flex: 1,
                                    padding: '14px',
                                    border: 'none',
                                    borderRadius: '10px',
                                    background: C.primary,
                                    color: 'white',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Save Settings
                            </button>
                        </div>
                    </div>
                </Modal>
   )}

<Notification
                isOpen={notification.show}
                onClose={() => setNotification({ ...notification, show: false })}
                type={notification.type}
                storyTitle={notification.storyTitle}
                errorMessage={notification.errorMessage}
            />
        </div>
    );
};

// Reusable Modal Component
// Modal Component
const Modal: React.FC<{
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    isMobile?: boolean;
}> = ({ onClose, title, children, isMobile = false }) => (
    <div onClick={onClose} style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(37,47,60,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        padding: isMobile ? '20px' : '0'
    }}>
        <div onClick={(e) => e.stopPropagation()} style={{
            background: C.surfaceLowest, borderRadius: '24px', padding: isMobile ? '28px 20px' : '40px',
            maxWidth: isMobile ? '100%' : '600px', width: isMobile ? '100%' : '90%',
            maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
            <h2 style={{
                fontFamily: HEADING, fontSize: isMobile ? '1.5rem' : '1.9rem', fontWeight: 900,
                margin: isMobile ? '0 0 20px' : '0 0 28px', color: C.onBackground
            }}>
                {title}
            </h2>
            {children}
        </div>
    </div>
);

export default Profile;